// src/TakeawayGym.js
import React, { useMemo, useRef, useState, useEffect } from 'react';
import { exercises } from './exerciseData';
import { ExerciseCardWithDynamicThumbnail } from './VideoThumbnailGenerator';

// --- Voice Visualizer Component ---
const VoiceVisualizer = ({ isListening, volume }) => {
  if (!isListening) return null;
  const bubbleScale = 1 + volume * 0.7;
  return (
    <div style={styles.visualizerOverlay}>
      <div style={styles.visualizerBubbleContainer}>
        <div style={{...styles.visualizerBubble, transform: `scale(${bubbleScale})`}}></div>
        <div style={styles.visualizerText}>Listening...</div>
      </div>
    </div>
  );
};

// --- COMPREHENSIVE AI Search "Brain": Maps user intent to exercise tags ---
const searchConceptMap = {
  // Activities & Life Goals
  hike: ["strength", "cardio", "balance", "stability", "achievement", "legs"],
  hiking: ["strength", "cardio", "balance", "stability", "achievement", "legs"],
  walk: ["strength", "cardio", "balance", "stability", "legs"],
  walking: ["strength", "cardio", "balance", "stability", "legs"],
  run: ["strength", "cardio", "progressive", "legs"],
  running: ["strength", "cardio", "progressive", "legs"],
  climb: ["strength", "balance", "stability", "legs", "cardio"],
  stairs: ["strength", "balance", "stability", "legs", "knee"],
  garden: ["strength", "flexibility", "knee", "back", "balance", "bending"],
  gardening: ["strength", "flexibility", "knee", "back", "balance", "bending"],
  cook: ["standing", "balance", "strength", "daily", "arms"],
  cooking: ["standing", "balance", "strength", "daily", "arms"],
  shop: ["walking", "strength", "endurance", "stability", "carry"],
  shopping: ["walking", "strength", "endurance", "stability", "carry"],
  clean: ["strength", "flexibility", "back", "shoulders", "reach"],
  cleaning: ["strength", "flexibility", "back", "shoulders", "reach"],
  drive: ["back", "neck", "seated", "stiffness", "posture"],
  driving: ["back", "neck", "seated", "stiffness", "posture"],
  travel: ["seated", "circulation", "stiffness", "ankle"],
  
  // Goals & Aspirations
  stronger: ["strength", "progressive", "goals", "achievement"],
  strength: ["strength", "progressive", "goals", "achievement"],
  fit: ["strength", "cardio", "progressive", "wellness"],
  fitness: ["strength", "cardio", "progressive", "wellness"],
  endurance: ["cardio", "breathing", "progressive", "stamina"],
  flexible: ["stretch", "gentle", "wellness", "therapeutic", "mobility"],
  flexibility: ["stretch", "gentle", "wellness", "therapeutic", "mobility"],
  mobility: ["stretch", "gentle", "wellness", "movement", "flexibility"],
  independence: ["strength", "balance", "daily", "achievement", "stability"],
  recover: ["gentle", "therapeutic", "rehabilitation", "healing", "recovery"],
  recovery: ["gentle", "therapeutic", "rehabilitation", "healing", "recovery"],
  
  // Balance & Safety
  balance: ["balance", "stability", "falls", "coordination", "prevention"],
  fall: ["balance", "stability", "falls", "coordination", "prevention"],
  falls: ["balance", "stability", "falls", "coordination", "prevention"],
  falling: ["balance", "stability", "falls", "coordination", "prevention"],
  stable: ["balance", "stability", "coordination", "strength"],
  stability: ["balance", "stability", "coordination", "strength"],
  steady: ["balance", "stability", "coordination", "strength"],
  safe: ["safety", "prevention", "gentle", "stability", "balance"],
  safety: ["safety", "prevention", "gentle", "stability", "balance"],
  
  // Pain & Symptoms
  pain: ["pain", "relief", "therapeutic", "gentle", "comfort"],
  painful: ["pain", "relief", "therapeutic", "gentle", "comfort"],
  ache: ["pain", "relief", "therapeutic", "gentle", "comfort"],
  aching: ["pain", "relief", "therapeutic", "gentle", "comfort"],
  hurt: ["pain", "relief", "therapeutic", "gentle", "comfort"],
  hurting: ["pain", "relief", "therapeutic", "gentle", "comfort"],
  sore: ["pain", "relief", "therapeutic", "gentle", "stretch"],
  soreness: ["pain", "relief", "therapeutic", "gentle", "stretch"],
  stiff: ["stretch", "gentle", "wellness", "therapeutic", "mobility"],
  stiffness: ["stretch", "gentle", "wellness", "therapeutic", "mobility"],
  tight: ["stretch", "gentle", "wellness", "therapeutic", "mobility"],
  tightness: ["stretch", "gentle", "wellness", "therapeutic", "mobility"],
  
  // Body Parts
  knee: ["gentle", "seated", "stability", "strength", "relief", "legs"],
  knees: ["gentle", "seated", "stability", "strength", "relief", "legs"],
  back: ["gentle", "seated", "stability", "strength", "relief", "stretch", "spine"],
  spine: ["gentle", "seated", "stability", "strength", "relief", "stretch", "back"],
  neck: ["gentle", "seated", "relief", "stretch", "posture"],
  shoulder: ["gentle", "seated", "relief", "stretch", "arms"],
  shoulders: ["gentle", "seated", "relief", "stretch", "arms"],
  hip: ["gentle", "seated", "stability", "strength", "relief", "legs"],
  hips: ["gentle", "seated", "stability", "strength", "relief", "legs"],
  ankle: ["balance", "stability", "mobility", "gentle", "feet"],
  ankles: ["balance", "stability", "mobility", "gentle", "feet"],
  foot: ["balance", "stability", "mobility", "gentle", "ankle"],
  feet: ["balance", "stability", "mobility", "gentle", "ankle"],
  leg: ["strength", "stability", "mobility", "balance", "walking"],
  legs: ["strength", "stability", "mobility", "balance", "walking"],
  arm: ["strength", "mobility", "reaching", "gentle"],
  arms: ["strength", "mobility", "reaching", "gentle"],
  wrist: ["gentle", "mobility", "relief", "flexibility"],
  hand: ["gentle", "mobility", "relief", "flexibility"],
  
  // Wellness & Mood
  relax: ["wellness", "mood", "gentle", "breathing", "comfort"],
  relaxation: ["wellness", "mood", "gentle", "breathing", "comfort"],
  stress: ["wellness", "mood", "gentle", "breathing", "comfort"],
  stressed: ["wellness", "mood", "gentle", "breathing", "comfort"],
  anxiety: ["wellness", "mood", "gentle", "breathing", "comfort"],
  anxious: ["wellness", "mood", "gentle", "breathing", "comfort"],
  calm: ["wellness", "mood", "gentle", "breathing", "comfort"],
  tired: ["energy", "gentle", "mood", "breathing", "wellness"],
  fatigue: ["energy", "gentle", "mood", "breathing", "wellness"],
  energy: ["cardio", "mood", "wellness", "breathing", "vitality"],
  energize: ["cardio", "mood", "wellness", "breathing", "vitality"],
  
  // Time & Routine
  daily: ["daily", "gentle", "wellness", "routine"],
  morning: ["daily", "gentle", "wellness", "mood", "energy"],
  evening: ["gentle", "wellness", "relaxation", "stretch"],
  quick: ["gentle", "daily", "seated", "short"],
  warmup: ["gentle", "mobility", "breathing", "preparation"],
  
  // Breathing & Cardio
  breath: ["breathing", "wellness", "cardio", "gentle"],
  breathe: ["breathing", "wellness", "cardio", "gentle"],
  breathing: ["breathing", "wellness", "cardio", "gentle"],
  breathless: ["breathing", "cardio", "gentle", "endurance"],
  cardio: ["cardio", "breathing", "endurance", "heart"],
  heart: ["cardio", "breathing", "endurance", "circulation"],
  
  // Conditions
  arthritis: ["gentle", "pain", "relief", "mobility", "therapeutic"],
  sciatica: ["pain", "relief", "therapeutic", "nerve", "back"],
  nerve: ["pain", "relief", "therapeutic", "gentle", "mobility"],
  surgery: ["recovery", "rehabilitation", "gentle", "healing", "therapeutic"],
  operation: ["recovery", "rehabilitation", "gentle", "healing", "therapeutic"],
  
  // Movement Quality
  weak: ["strength", "progressive", "stability", "building"],
  weakness: ["strength", "progressive", "stability", "building"],
  dizzy: ["balance", "seated", "gentle", "stability", "coordination"],
  dizziness: ["balance", "seated", "gentle", "stability", "coordination"],
  coordination: ["balance", "stability", "coordination", "movement"],
  posture: ["strength", "stability", "back", "core", "alignment"],
};

// --- Multi-word phrase matching ---
const phraseMap = {
  "get out of chair": ["strength", "legs", "balance", "stability"],
  "getting up": ["strength", "legs", "balance", "stability"],
  "climb stairs": ["strength", "legs", "cardio", "knee"],
  "climbing stairs": ["strength", "legs", "cardio", "knee"],
  "reach overhead": ["shoulders", "arms", "mobility", "strength"],
  "reaching up": ["shoulders", "arms", "mobility", "strength"],
  "tie shoes": ["flexibility", "balance", "back", "hip"],
  "tying shoes": ["flexibility", "balance", "back", "hip"],
  "get dressed": ["mobility", "balance", "flexibility", "independence"],
  "getting dressed": ["mobility", "balance", "flexibility", "independence"],
  "pick up": ["balance", "back", "legs", "flexibility"],
  "picking up": ["balance", "back", "legs", "flexibility"],
  "sit down": ["strength", "balance", "legs", "control"],
  "sitting down": ["strength", "balance", "legs", "control"],
};

const TakeawayGym = () => {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [query, setQuery] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isSpeechSupported, setIsSpeechSupported] = useState(false);
  const [volumeLevel, setVolumeLevel] = useState(0);
  const recognitionRef = useRef(null);
  const audioContextRef = useRef(null);
  const streamRef = useRef(null);
  const animationFrameRef = useRef(null);

  const cleanUpAudio = () => {
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') audioContextRef.current.close();
    if (streamRef.current) streamRef.current.getTracks().forEach(track => track.stop());
    setVolumeLevel(0);
  };

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      setIsSpeechSupported(true);
      const recognition = new SpeechRecognition();
      recognition.lang = 'en-IN';
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;
      recognition.onstart = () => { console.log('Speech started'); setIsListening(true); };
      recognition.onend = () => { console.log('Speech ended'); setIsListening(false); cleanUpAudio(); };
      recognition.onerror = (event) => {
        console.error('Speech error:', event.error);
        setIsListening(false);
        cleanUpAudio();
        if (event.error === 'no-speech') alert('No speech detected. Please try again.');
        else if (event.error === 'audio-capture') alert('No microphone detected. Check device settings.');
      };
      recognition.onresult = (event) => {
        console.log('Result received');
        const transcript = event.results[event.results.length - 1][0].transcript;
        console.log('Transcript:', transcript);
        setQuery(transcript);
      };
      recognitionRef.current = recognition;
    }
    return () => cleanUpAudio();
  }, []);

  const visualizeAudio = (stream) => {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    audioContextRef.current = audioContext;
    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    source.connect(analyser);
    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    const draw = () => {
      animationFrameRef.current = requestAnimationFrame(draw);
      analyser.getByteFrequencyData(dataArray);
      const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
      setVolumeLevel(Math.min(avg / 128, 1.0));
    };
    draw();
  };

  const handleVoiceSearch = async () => {
    if (isListening) {
      recognitionRef.current?.stop();
    } else {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = stream;
        visualizeAudio(stream);
        recognitionRef.current?.start();
      } catch (err) {
        console.error("Mic access denied:", err);
        alert("Microphone access required. Enable it in browser settings.");
      }
    }
  };

  const healthCategories = [
    { title: "A Daily Dose of Joy", subtitle: "Wellness exercises to brighten your day", tags: ["wellness", "mood", "gentle", "daily"], color: "#FF9933" },
    { title: "Your Next Summit - Dream it, Achieve it", subtitle: "Goal-oriented exercises for your aspirations", tags: ["goals", "strength", "progressive", "achievement"], color: "#138808" },
    { title: "Stay Strong, Stay Safe", subtitle: "Injury prevention and protective exercises", tags: ["prevention", "safety", "stability", "strength"], color: "#000080" },
    { title: "Find Your Relief", subtitle: "Pain management and comfort exercises", tags: ["pain", "relief", "therapeutic", "comfort"], color: "#9C27B0" },
    { title: "Breath, Beat, Balance", subtitle: "Heart, lung, and neurological wellness", tags: ["cardio", "breathing", "balance", "neurological"], color: "#FF5722" },
    { title: "Your Comeback Story", subtitle: "Post-operation recovery exercises", tags: ["recovery", "rehabilitation", "post-surgery", "healing"], color: "#795548" },
    { title: "The Balance Club", subtitle: "Fall prevention and stability training", tags: ["balance", "falls", "stability", "coordination"], color: "#607D8B" }
  ];

  const enhancedExercises = useMemo(() => {
    return exercises.map(exercise => {
      const assignedTags = [];
      const text = `${exercise.title} ${exercise.description}`.toLowerCase();
      if (text.includes('gentle') || text.includes('seated') || text.includes('comfort') || text.includes('yoga') || text.includes('relaxing')) assignedTags.push('wellness', 'mood', 'gentle', 'daily');
      if (text.includes('cardio') || text.includes('circuit') || text.includes('strength') || text.includes('build') || exercise.difficulty === 'Intermediate') assignedTags.push('goals', 'strength', 'progressive', 'achievement');
      if (text.includes('standing') || text.includes('stability') || text.includes('safe') || text.includes('fall') || text.includes('prevention')) assignedTags.push('prevention', 'safety', 'stability', 'strength');
      if (text.includes('stretch') || text.includes('nerve') || text.includes('relief') || text.includes('hamstring') || text.includes('neck')) assignedTags.push('pain', 'relief', 'therapeutic', 'comfort');
      if (text.includes('cardio') || text.includes('running') || text.includes('marching') || text.includes('exhale') || text.includes('inhale')) assignedTags.push('cardio', 'breathing', 'balance');
      if (text.includes('rehabilitation') || text.includes('recovery') || text.includes('therapeutic')) assignedTags.push('recovery', 'rehabilitation', 'healing');
      if (text.includes('balance') || text.includes('heel') || text.includes('toe') || text.includes('standing') || text.includes('coordination')) assignedTags.push('balance', 'falls', 'stability', 'coordination');
      return { ...exercise, tags: [...new Set(assignedTags)] };
    });
  }, []);

  const getExercisesByCategory = (categoryTags) => enhancedExercises.filter(exercise => exercise.tags.some(tag => categoryTags.includes(tag)));

  const normalizedQuery = query.trim().toLowerCase();
  
  // --- ENHANCED SEARCH ALGORITHM ---
  const searchResults = useMemo(() => {
    if (!normalizedQuery || normalizedQuery.length < 3) return [];
    
    const queryWords = new Set(normalizedQuery.split(/\s+/).filter(w => w.length > 2));
    const exerciseScores = new Map();
    const relevantTags = new Set();
  
    // 1. Check for phrase matches first
    for (const [phrase, tags] of Object.entries(phraseMap)) {
      if (normalizedQuery.includes(phrase)) {
        tags.forEach(tag => relevantTags.add(tag));
      }
    }
  
    // 2. Expand search using searchConceptMap (word-level)
    for (const word of queryWords) {
      if (searchConceptMap[word]) {
        searchConceptMap[word].forEach(tag => relevantTags.add(tag));
      }
    }
  
    enhancedExercises.forEach(exercise => {
      let score = 0;
  
      // ✅ 1. Check against existing tags (highest priority)
      exercise.tags.forEach(tag => { 
        if (relevantTags.has(tag)) score += 15; 
      });
  
      // ✅ 2. Title matching (very important)
      queryWords.forEach(word => {
        if (exercise.title.toLowerCase().includes(word)) score += 8;
      });
  
      // ✅ 3. Structured field matching (primary search source)
      const checkStructuredFields = (fields, weight) => {
        if (!fields) return;
        fields.forEach(field => {
          const fieldLower = field.toLowerCase();
          queryWords.forEach(word => {
            if (fieldLower.includes(word)) {
              score += weight;
            } else if (word.length > 3 && fieldLower.includes(word.slice(0, -1))) {
              score += weight * 0.7; // partial match for plurals/tenses
            }
          });
        });
      };
  
      // Weight by importance
      checkStructuredFields(exercise.goalIntent, 10);          // What user wants to achieve
      checkStructuredFields(exercise.symptomFeeling, 10);      // What user is experiencing
      checkStructuredFields(exercise.activityContext, 8);      // Activity preparation
      checkStructuredFields(exercise.bodyPart, 7);             // Body area focus
      checkStructuredFields(exercise.exerciseType, 5);         // Type of exercise
      checkStructuredFields(exercise.difficultyIntensity, 3);  // Intensity level
  
      // ✅ 4. Description (lower priority)
      queryWords.forEach(word => {
        if (exercise.description.toLowerCase().includes(word)) score += 3;
      });
  
      // ✅ 5. Category matching
if (exercise.category) {
  // Ensure category is always an array
  const categories = Array.isArray(exercise.category) ? exercise.category : [exercise.category];

  categories.forEach(cat => {
    queryWords.forEach(word => {
      if (cat.toLowerCase().includes(word.toLowerCase())) score += 4;
    });
  });
}
  
      // Bonus for strong multi-field matches
      if (score > 20) score += 10;
      if (score > 40) score += 15;
      
      if (score > 0) exerciseScores.set(exercise, score);
    });
  
    return Array.from(exerciseScores.entries())
      .sort((a, b) => b[1] - a[1])
      .map(entry => entry[0]);
  }, [normalizedQuery, enhancedExercises]);

  const openVideoModal = (exercise) => setSelectedVideo(exercise);
  const closeVideoModal = () => setSelectedVideo(null);
  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner': return '#4CAF50';
      case 'Intermediate': return '#FF9800';
      case 'Advanced': return '#F44336';
      default: return '#9E9E9E';
    }
  };

  const getSearchInputPadding = () => {
    let paddingRight = '3.5rem';
    if (isSpeechSupported && query) paddingRight = '9.5rem';
    else if (isSpeechSupported) paddingRight = '7rem';
    else if (query) paddingRight = '5.5rem';
    return { ...styles.searchInput, paddingRight };
  };

  return (
    <div style={styles.netflixContainer}>
      <VoiceVisualizer isListening={isListening} volume={volumeLevel} />
      <header style={styles.heroSection}>
        <div style={styles.heroContent}>
          <h1 style={styles.heroTitle}>Takeaway Gym</h1>
          <p style={styles.heroSubtitle}>Your personalized exercise library designed for healthy aging. Start your wellness journey with expert-curated videos.</p>
          <div style={styles.heroStats}>
            <span style={styles.stat}>{enhancedExercises.length} Videos</span>
            <span style={styles.stat}>7 Categories</span>
            <span style={styles.stat}>Expert Approved</span>
          </div>
        </div>
      </header>
      <section style={styles.searchSection}>
        <div style={styles.searchContainer}>
          <form onSubmit={(e) => e.preventDefault()} style={styles.searchBarWrapper} role="search" aria-label="Search exercises">
            <input type="search" placeholder="Describe your goal, e.g., 'prepare for a hike' or 'help with back pain'" value={query} onChange={(e) => setQuery(e.target.value)} style={getSearchInputPadding()} />
            {query && <button type="button" onClick={() => setQuery('')} aria-label="Clear search" style={styles.clearBtn} title="Clear">×</button>}
            {isSpeechSupported && (
              <button type="button" onClick={handleVoiceSearch} aria-label="Search by voice" style={{...styles.micBtn, right: query ? '6.5rem' : '3.8rem', color: isListening ? '#FF5722' : '#138808', backgroundColor: isListening ? 'rgba(255, 87, 34, 0.1)' : 'rgba(19, 136, 8, 0.1)', borderRadius: '50%', width: '2.5rem', height: '2.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center'}} title="Search by voice">🎤</button>
            )}
            <button type="submit" style={styles.searchBtn} aria-label="Search">🔍</button>
          </form>
        </div>
      </section>
      <main style={styles.rowsWrapper} className="content-section">
        {normalizedQuery && <SearchResultsRow query={normalizedQuery} results={searchResults} onPlay={openVideoModal} />}
        {healthCategories.map((category) => {
          const categoryExercises = getExercisesByCategory(category.tags);
          if (categoryExercises.length === 0) return null;
          return <CategoryRow key={category.title} category={category} exercises={categoryExercises} onPlay={openVideoModal} />;
        })}
      </main>
      {selectedVideo && <VideoModal video={selectedVideo} onClose={closeVideoModal} getDifficultyColor={getDifficultyColor} />}
    </div>
  );
};

const SearchResultsRow = ({ query, results, onPlay }) => {
  const scrollerRef = useRef(null);
  const scrollByCardWidths = (dir = 1) => {
    const node = scrollerRef.current;
    if (!node) return;
    const card = node.querySelector('.video-card');
    const width = card ? card.getBoundingClientRect().width : 280;
    node.scrollBy({ left: dir * width * 3, behavior: 'smooth' });
  };
  const hasQuery = query.length >= 3;
  return (
    <section style={styles.searchResultsSection}>
      <div style={styles.searchHeader}>
        <h2 style={styles.searchTitle}>{hasQuery ? `Recommended for you (${results.length})` : "Keep typing to see recommendations..."}</h2>
      </div>
      {hasQuery && results.length === 0 && (
        <div style={styles.noResults}>
          <p>We couldn't find specific exercises for your search.</p>
          <p style={styles.noResultsSubtext}>Try rephrasing, or browse the categories below.</p>
        </div>
      )}
      {hasQuery && results.length > 0 && (
        <>
          <div style={styles.sectionHeader}>
            <p style={styles.categorySubtitle}>Based on your search for: "{query}"</p>
            <div style={styles.rowControls}>
              <button style={styles.navPill} onClick={() => scrollByCardWidths(-1)} aria-label="Scroll left">◀</button>
              <button style={styles.navPill} onClick={() => scrollByCardWidths(1)} aria-label="Scroll right">▶</button>
            </div>
          </div>
          <div style={styles.rowScroller} ref={scrollerRef}>
            {results.map((exercise, idx) => <ExerciseCardWithDynamicThumbnail key={`search-${exercise.title}-${idx}`} exercise={exercise} onPlay={onPlay} thumbnailTimestamp={exercise.thumbnailTimestamp || 5} />)}
          </div>
        </>
      )}
    </section>
  );
};

const CategoryRow = ({ category, exercises, onPlay }) => {
  const scrollerRef = useRef(null);
  const scrollByCardWidths = (dir = 1) => {
    const node = scrollerRef.current;
    if (!node) return;
    const card = node.querySelector('.video-card');
    const width = card ? card.getBoundingClientRect().width : 280;
    node.scrollBy({ left: dir * width * 3, behavior: 'smooth' });
  };
  return (
    <section style={styles.categorySection}>
      <div style={styles.categoryHeader}>
        <div>
          <h2 style={{...styles.categoryTitle, color: category.color}}>{category.title}</h2>
          <p style={styles.categorySubtitle}>{category.subtitle}</p>
        </div>
        <div style={styles.rowControls}>
          <button style={{...styles.navPill, backgroundColor: category.color}} onClick={() => scrollByCardWidths(-1)} aria-label="Scroll left">◀</button>
          <button style={{...styles.navPill, backgroundColor: category.color}} onClick={() => scrollByCardWidths(1)} aria-label="Scroll right">▶</button>
        </div>
      </div>
      <div style={styles.rowScroller} ref={scrollerRef}>
        {exercises.map((exercise, idx) => <ExerciseCardWithDynamicThumbnail key={`${category.title}-${exercise.title}-${idx}`} exercise={exercise} onPlay={onPlay} categoryColor={category.color} thumbnailTimestamp={exercise.thumbnailTimestamp || 5} />)}
      </div>
    </section>
  );
};

const VideoModal = ({ video, onClose, getDifficultyColor }) => (
  <div style={styles.modalOverlay} role="dialog" aria-modal="true" onClick={onClose}>
    <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
      <button
        style={styles.closeButton}
        onClick={onClose}
        className="close-button"
        aria-label="Close"
      >
        ×
      </button>
      <div style={styles.modalVideoWrapper}>
        <video
          style={styles.modalVideo}
          src={video.videoSrc}
          controls
          autoPlay
          controlsList="nodownload noplaybackrate"
          disablePictureInPicture
          onContextMenu={(e) => e.preventDefault()}
        />
      </div>
      <div style={styles.modalInfo}>
        <h3 style={styles.modalTitle}>{video.title}</h3>
        <div style={styles.modalMeta}>
          <span style={styles.modalDuration}>{video.duration}</span>
          <span
            style={{
              ...styles.modalDifficulty,
              color: getDifficultyColor(video.difficulty),
            }}
          >
            {video.difficulty}
          </span>
          <span style={styles.categoryTag}>{video.category}</span>
        </div>
        <p style={styles.modalDescription}>{video.description}</p>
        {video.tags.length > 0 && (
          <div style={styles.tagsContainer}>
            <h4 style={styles.tagsTitle}>Health Categories:</h4>
            <div style={styles.tagsList}>
              {video.tags.map((tag, idx) => (
                <span key={idx} style={styles.tag}>
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
        <div style={styles.disclaimer}>
          ⚠️ Important: Please consult with your physiotherapist before starting
          any new exercise routine.
        </div>
      </div>
    </div>
  </div>
);


const styles = {
  visualizerOverlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 },
  visualizerBubbleContainer: { position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  visualizerBubble: { width: '150px', height: '150px', backgroundColor: 'rgba(255, 153, 51, 0.8)', borderRadius: '50%', transition: 'transform 0.1s ease-out', border: '4px solid rgba(255, 255, 255, 0.5)', boxShadow: '0 0 30px rgba(255, 153, 51, 0.5)' },
  visualizerText: { position: 'absolute', color: 'white', fontSize: '1.2rem', fontWeight: 'bold', textShadow: '0 1px 3px rgba(0,0,0,0.5)' },
  netflixContainer: { width: '100%', height: '100vh', backgroundColor: '#ffffff', color: '#333333', fontFamily: 'Netflix Sans, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif', overflow: 'auto', position: 'relative' },
  heroSection: { background: 'linear-gradient(135deg, #FF9933 0%, #ffffff 50%, #138808 100%)', padding: '2rem 2rem 2rem', textAlign: 'center', position: 'relative', overflow: 'hidden', borderBottom: '4px solid #000080', marginBottom: '0' },
  heroContent: { maxWidth: '900px', margin: '0 auto', position: 'relative', zIndex: 2 },
  heroTitle: { fontSize: 'clamp(2.5rem, 5vw, 4rem)', fontWeight: 700, marginBottom: '0.5rem', background: 'linear-gradient(45deg, #FF9933, #000080)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', textShadow: '0 2px 4px rgba(0,0,0,0.1)' },
  heroSubtitle: { fontSize: '1.15rem', lineHeight: 1.6, marginBottom: '1.25rem', color: '#333333', maxWidth: '700px', marginInline: 'auto', fontWeight: 500 },
  heroStats: { display: 'flex', justifyContent: 'center', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.25rem' },
  stat: { fontSize: '0.95rem', color: '#ffffff', fontWeight: 600, border: '2px solid #FF9933', padding: '0.4rem 0.9rem', borderRadius: 20, backgroundColor: '#FF9933', boxShadow: '0 2px 8px rgba(255, 153, 51, 0.3)' },
  searchSection: { backgroundColor: '#f8f9fa', borderBottom: '3px solid #138808', padding: '2rem 1.25rem', position: 'sticky', top: 0, zIndex: 100 },
  searchContainer: { maxWidth: '900px', margin: '0 auto' },
  searchBarWrapper: { display: 'flex', alignItems: 'center', position: 'relative', width: '100%' },
  searchInput: { width: '100%', fontSize: '1.2rem', padding: '1rem 3.25rem 1rem 1.25rem', borderRadius: '999px', border: '2px solid #138808', outline: 'none', boxShadow: '0 3px 10px rgba(19, 136, 8, 0.15)', backgroundColor: '#ffffff', transition: 'padding-right 0.3s ease' },
  searchBtn: { position: 'absolute', right: '0.6rem', top: '50%', transform: 'translateY(-50%)', background: '#138808', color: '#fff', border: '2px solid #0d5c06', borderRadius: '999px', padding: '0.55rem 0.9rem', cursor: 'pointer', fontSize: '1.1rem' },
  clearBtn: { position: 'absolute', right: '3.2rem', top: '50%', transform: 'translateY(-50%)', background: 'transparent', color: '#333', border: 'none', cursor: 'pointer', fontSize: '1.5rem', lineHeight: 1 },
  micBtn: { position: 'absolute', top: '50%', transform: 'translateY(-50%)', background: 'transparent', color: '#333', border: '2px solid transparent', cursor: 'pointer', fontSize: '1.6rem', lineHeight: 1, padding: '0.5rem', transition: 'all 0.2s ease' },
  rowsWrapper: { padding: '2rem 1.25rem 3rem' },
  searchResultsSection: { marginBottom: '3rem', padding: '2rem', backgroundColor: '#ffffff', borderRadius: '12px', border: '2px solid #138808', boxShadow: '0 4px 12px rgba(19, 136, 8, 0.1)' },
  searchHeader: { marginBottom: '1.5rem' },
  searchTitle: { fontSize: '1.6rem', fontWeight: 700, color: '#138808', margin: 0 },
  noResults: { textAlign: 'center', padding: '2rem', color: '#666' },
  noResultsSubtext: { fontSize: '0.9rem', color: '#999', marginTop: '0.5rem' },
  categorySection: { marginBottom: '3rem' },
  categoryHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '1rem', marginBottom: '1rem' },
  categoryTitle: { fontSize: '1.8rem', fontWeight: 700, margin: 0, marginBottom: '0.25rem' },
  categorySubtitle: { fontSize: '1rem', color: '#666', margin: 0, fontWeight: 400 },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem', flexWrap: 'wrap' },
  rowControls: { display: 'flex', gap: '0.5rem' },
  navPill: { background: '#138808', color: '#fff', border: 'none', padding: '0.4rem 0.7rem', borderRadius: 999, cursor: 'pointer', fontSize: '1rem', boxShadow: '0 2px 6px rgba(19,136,8,.25)' },
  rowScroller: { display: 'grid', gridAutoFlow: 'column', gridAutoColumns: '260px', gap: '1rem', overflowX: 'auto', overscrollBehaviorX: 'contain', scrollSnapType: 'x proximity', paddingBottom: '0.25rem' },
  modalOverlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '2rem' },
  modalContent: { backgroundColor: '#ffffff', borderRadius: '12px', maxWidth: '900px', width: '100%', maxHeight: '90vh', overflow: 'auto', position: 'relative', border: '3px solid #000080' },
  closeButton: { position: 'absolute', top: '1rem', right: '1rem', backgroundColor: '#FF9933', color: '#ffffff', border: 'none', width: 40, height: 40, borderRadius: '50%', fontSize: '1.5rem', cursor: 'pointer', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background-color 0.3s ease', boxShadow: '0 2px 8px rgba(255, 153, 51, 0.4)' },
  modalVideoWrapper: { width: '100%', aspectRatio: '16 / 9', background: '#000' },
  modalVideo: { width: '100%', height: '100%', border: 'none', objectFit: 'contain' },
  modalInfo: { padding: '1.25rem 1.5rem 2rem' },
  modalTitle: { fontSize: '1.6rem', fontWeight: 700, marginBottom: '0.5rem', color: '#000080' },
  modalMeta: { display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem', flexWrap: 'wrap' },
  modalDuration: { fontSize: '1rem', color: '#666666' },
  modalDifficulty: { fontSize: '1rem', fontWeight: 600 },
  modalDescription: { fontSize: '1.05rem', lineHeight: 1.6, color: '#333333', marginBottom: '1rem' },
  categoryTag: { color: '#666666', fontSize: '0.85rem' },
  tagsContainer: { marginBottom: '1rem' },
  tagsTitle: { fontSize: '1rem', fontWeight: 600, marginBottom: '0.5rem', color: '#333' },
  tagsList: { display: 'flex', flexWrap: 'wrap', gap: '0.5rem' },
  tag: { backgroundColor: '#e9ecef', color: '#495057', padding: '0.25rem 0.75rem', borderRadius: '20px', fontSize: '0.85rem', fontWeight: 500 },
  disclaimer: { backgroundColor: 'rgba(255, 153, 51, 0.1)', border: '2px solid #FF9933', borderRadius: '8px', padding: '1rem', fontSize: '0.95rem', color: '#cc7a00', lineHeight: 1.5, fontWeight: 500 }
};

const addHoverEffects = () => {
  const styleId = 'enhanced-takeaway-gym-hover-styles';
  if (document.getElementById(styleId)) return;
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `.video-card:hover { transform: scale(1.04); border-color: #FF9933; box-shadow: 0 8px 25px rgba(255, 153, 51, 0.2); } .video-card:hover .thumbnail { transform: scale(1.06); } .video-card:hover .play-overlay { opacity: 1; } .video-card:hover .play-button { transform: scale(1); background-color: #138808; } .close-button:hover { background-color: #138808 !important; }`;
  document.head.appendChild(style);
};
if (typeof document !== 'undefined') addHoverEffects();

export default TakeawayGym;